import React, { useState } from 'react';
import { X, Mail, Copy, Check, AlertCircle } from 'lucide-react';
import { generateTechnicianCredentials } from '../../lib/auth';

interface TechnicianInviteDialogProps {
  onClose: () => void;
  onInvite: (email: string) => void;
}

export const TechnicianInviteDialog: React.FC<TechnicianInviteDialogProps> = ({ onClose, onInvite }) => {
  const [email, setEmail] = useState('');
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      if (!email) {
        setError('Bitte geben Sie eine E-Mail-Adresse ein');
        return;
      }
      
      // Generate credentials
      const newCredentials = await generateTechnicianCredentials(email);
      setCredentials(newCredentials);
      
      // Notify parent component
      onInvite(email);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten');
    }
  };

  const copyToClipboard = () => {
    if (!credentials) return;
    
    const text = `Ihre Zugangsdaten für InstallTrack Pro:

E-Mail: ${credentials.email}
Passwort: ${credentials.password}

Bitte ändern Sie Ihr Passwort nach der ersten Anmeldung.`;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            <span>Techniker einladen</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!credentials ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-md flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail-Adresse des Technikers
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg"
                placeholder="techniker@example.com"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Zugangsdaten erstellen
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-2">
                Zugangsdaten erstellt!
              </h3>
              <p className="text-sm text-green-700">
                Die Zugangsdaten wurden erfolgreich erstellt. Bitte teilen Sie diese sicher mit dem Techniker.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail
                </label>
                <div className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                  {credentials.email}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort
                </label>
                <div className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200 font-mono">
                  {credentials.password}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Kopiert!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    <span>Zugangsdaten kopieren</span>
                  </>
                )}
              </button>

              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};